import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAdminControls } from "../../state/AdminControlsContext";
import type { AdminTimeRange } from "../../graphql/types";
import { formatShortDate } from "../../utils/format";

const PRESETS: { key: AdminTimeRange; labelKey: string }[] = [
  { key: "DAY", labelKey: "topbar.range.day" },
  { key: "WEEK", labelKey: "topbar.range.week" },
  { key: "MONTH", labelKey: "topbar.range.month" },
];

/**
 * Topbar time-range control: a "Zeitraum" calendar button with a Von/Bis
 * popover (→ range=CUSTOM) to the left of the Tag/Woche/Monat segmented
 * toggle. Reads/writes the shared AdminControls; the Dashboard consumes
 * `rangeVars` from the same context.
 */
export const TimeRangeControl: React.FC = () => {
  const { t } = useTranslation();
  const { timeRange, customFrom, customTo, setPresetRange, applyCustomRange, clearCustomRange } =
    useAdminControls();

  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(customFrom ?? "");
  const [to, setTo] = useState(customTo ?? "");

  const isCustom = timeRange === "CUSTOM";
  const customLabel =
    isCustom && customFrom && customTo
      ? `${formatShortDate(customFrom)} – ${formatShortDate(customTo)}`
      : t("topbar.range.label");

  const canApply = from !== "" && to !== "" && from <= to;

  const onApply = () => {
    if (!canApply) return;
    applyCustomRange(from, to);
    setOpen(false);
  };

  const onReset = () => {
    setFrom("");
    setTo("");
    clearCustomRange();
    setOpen(false);
  };

  return (
    <div className="range-control">
      <div className="range-custom">
        <button
          type="button"
          className={`range-custom-btn${isCustom ? " range-custom-btn--active" : ""}`}
          onClick={() => setOpen((v) => !v)}
        >
          <CalendarIcon />
          <span>{customLabel}</span>
        </button>

        {open && (
          <>
            <div className="range-popover-backdrop" onClick={() => setOpen(false)} />
            <div className="range-popover" role="dialog">
              <label className="range-field">
                <span>{t("topbar.range.from")}</span>
                <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
              </label>
              <label className="range-field">
                <span>{t("topbar.range.to")}</span>
                <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
              </label>
              <div className="range-popover-actions">
                <button type="button" className="range-btn range-btn--ghost" onClick={onReset}>
                  {t("topbar.range.reset")}
                </button>
                <button
                  type="button"
                  className="range-btn range-btn--primary"
                  onClick={onApply}
                  disabled={!canApply}
                >
                  {t("topbar.range.apply")}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="range-toggle">
        {PRESETS.map(({ key, labelKey }) => (
          <button
            key={key}
            type="button"
            className={`range-seg${timeRange === key ? " range-seg--active" : ""}`}
            onClick={() => setPresetRange(key)}
          >
            {t(labelKey)}
          </button>
        ))}
      </div>
    </div>
  );
};

const CalendarIcon: React.FC = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4.5" width="18" height="16" rx="3" />
    <path d="M3 9h18M8 2.5v4M16 2.5v4" />
  </svg>
);
